import { useEffect, useMemo, useState } from "react"

import { useTranslation } from "react-i18next"
import { useRecoilValue, useSetRecoilState } from "recoil"
import styled from "styled-components"

import useCurrentModuleEquipment from "~/api/hooks/useCurrentModuleEquipment"
import useUpdateModuleInverterEquipment from "~/api/hooks/useUpdateModuleInverterEquipment"
import { Dropdown } from "~/components/ui"
import {
  isSelectedModuleSavedState,
  selectedModuleRemoteState,
  selectedModuleState,
} from "~/containers/LegacyDesigner/state/elements/panels"
import type { IPart } from "~/containers/Proposal/Setup/Form/setup-steps-types"
import { useDesignContext } from "~/containers/Proposal/state/designs/design-context"
import { getIncompatibleSelectedInverters } from "~/containers/Proposal/state/equipment/module-inverter-compatibility"
import { useBaseEquipment } from "~/containers/Proposal/state/equipment/use-base-equipment"
import { useDispatchError } from "~/containers/Proposal/state/notifications/QueryErrorModal"
import { useProposalContext } from "~/containers/Proposal/state/proposal/proposal-context"
import { syncWaitingForEntityState } from "~/containers/Proposal/state/sync-state"

import { SetupMenuFormFooter } from "../SetupMenuFooter"
import { ModuleInverterCompatibilityWarning } from "./ModuleInverterCompatibilityWarning"

export const MultiChoiceSelectForm = styled.form`
  section > span > div {
    width: 100%;
    > h3 {
      font-size: 18px;
      font-weight: 500;
      margin-bottom: 20px;
    }
  }
`

const InverterSelection: IPart[`form`] = () => {
  const { t } = useTranslation()

  const design = useDesignContext()
  const proposal = useProposalContext()
  const selectedModule = useRecoilValue(selectedModuleState)
  const isSelectedModuleSaved = useRecoilValue(isSelectedModuleSavedState)
  const setSelectedModuleRemote = useSetRecoilState(selectedModuleRemoteState)
  const setSyncWaitingForDesign = useSetRecoilState(
    syncWaitingForEntityState(`design`),
  )

  const [selectedBrand, setSelectedBrand] = useState<string>(``)
  const [model, setModel] = useState<{
    id: number
    name: string
  } | null>(null)
  const [secondaryModel, setSecondaryModel] = useState<{
    id: number
    name: string
  } | null>(null)

  const onError = useDispatchError()

  const updateInverterMutation = useUpdateModuleInverterEquipment({
    onError,
  })

  const modulesQuery = useCurrentModuleEquipment(proposal.local.id, { onError })
  const currentSelectedModule = useMemo(
    () => modulesQuery.data?.modules.find(({ id }) => id === selectedModule.id),
    [modulesQuery.data, selectedModule.id],
  )
  const isSelectedModuleUnavailable =
    modulesQuery.isSuccess && currentSelectedModule === undefined
  const isCompatibilityReady =
    modulesQuery.isSuccess && currentSelectedModule !== undefined
  const selectedModuleData = currentSelectedModule?.moduleData
  const invertersData = useMemo(
    () => modulesQuery.data?.inverters ?? [],
    [modulesQuery.data?.inverters],
  )
  const currentInverterIds = useMemo(
    () => new Set(invertersData.map(({ id }) => id)),
    [invertersData],
  )
  const incompatibleInverterIds = useMemo(
    () =>
      new Set(
        (selectedModuleData?.incompatibleInverters ?? []).filter((id) =>
          currentInverterIds.has(id),
        ),
      ),
    [currentInverterIds, selectedModuleData?.incompatibleInverters],
  )
  const compatibleInverters = useMemo(
    () =>
      invertersData.filter(
        (inverter) => !incompatibleInverterIds.has(inverter.id),
      ),
    [incompatibleInverterIds, invertersData],
  )
  const brandsList = useMemo(() => {
    const compatibleBrands = new Set(
      compatibleInverters.map(({ manufacturer }) => manufacturer),
    )

    return [...compatibleBrands]
  }, [compatibleInverters])
  const modelOptionsList = useMemo(
    () =>
      compatibleInverters
        .filter((inverter) => inverter.manufacturer === selectedBrand)
        .map((inverter) => ({ id: inverter.id, name: inverter.name })),
    [compatibleInverters, selectedBrand],
  )

  useEffect(() => {
    setSelectedBrand(design.local.inverterManufacturer ?? ``)
  }, [setSelectedBrand, design.local.inverterManufacturer])

  useEffect(() => {
    if (model?.id && model?.id === secondaryModel?.id) {
      setSecondaryModel(null)
    }
  }, [model?.id, secondaryModel?.id, setSecondaryModel])

  // set the primary inverter model if one already exists on the design
  useEffect(() => {
    if (design.local.inverter?.name) {
      if (
        design.local.inverter.manufacturer?.name.toLocaleLowerCase() !==
        selectedBrand.toLocaleLowerCase()
      ) {
        return
      }
      const inverter = modelOptionsList.find(
        (op) => op.id === design.local.inverter?.id,
      )

      if (inverter) {
        setModel({
          id: design.local.inverter.id,
          name: inverter.name,
        })
      } else {
        setModel({
          id: design.local.inverter.id,
          name: design.local.inverter.name,
        })
      }
    }
  }, [design.local.inverter, setModel, modelOptionsList, selectedBrand])

  // set the secondary inverter model if one already exists on the design
  useEffect(() => {
    if (design.local.secondaryInverter?.name) {
      if (
        design.local.secondaryInverter.manufacturer?.name.toLocaleLowerCase() !==
        selectedBrand.toLocaleLowerCase()
      ) {
        setSecondaryModel(null)

        return
      }

      const inverter = modelOptionsList.find(
        (op) => op.id === design.local.secondaryInverter?.id,
      )
      if (inverter) {
        setSecondaryModel({
          id: design.local.secondaryInverter.id,
          name: inverter.name,
        })
      } else {
        setSecondaryModel({
          id: design.local.secondaryInverter.id,
          name: design.local.secondaryInverter.name,
        })
      }
    }

    if (!design.local.secondaryInverter?.name) {
      setSecondaryModel(null)
    }
  }, [
    design.local.secondaryInverter,
    setSecondaryModel,
    modelOptionsList,
    selectedBrand,
  ])

  const { isBaseInverter, createBaseBrandMatcher } = useBaseEquipment()
  const isBaseBrand = useMemo(
    () =>
      createBaseBrandMatcher(
        invertersData,
        (inv) => inv.id,
        (inv) => inv.manufacturer,
      ),
    [createBaseBrandMatcher, invertersData],
  )
  const incompatibleSelectedInverters = useMemo(
    () =>
      getIncompatibleSelectedInverters(
        selectedModuleData,
        [model, secondaryModel],
        currentInverterIds,
      ),
    [currentInverterIds, model, secondaryModel, selectedModuleData],
  )
  const hasIncompatibleSelection = incompatibleSelectedInverters.length > 0

  return (
    <MultiChoiceSelectForm id="inverter-selection">
      <section>
        <span className="flex flex-col justify-between items-start gap-[30px] min-h-[55px] relative min-[750px]:flex-row min-[750px]:gap-y-0 [&>div]:w-full [&>div]:overflow-hidden">
          <div>
            <Dropdown.Container
              disabled={!isCompatibilityReady}
              SelectedOption={() => <p>{selectedBrand}</p>}
              Label={() => <label>{t(`inverterBrand`)}</label>}
              Options={brandsList.map((brand) => {
                return (
                  <Dropdown.Item
                    key={brand}
                    onClick={() => {
                      setModel(null)
                      setSecondaryModel(null)
                      setSelectedBrand(brand)
                    }}
                    label1={brand}
                    isBase={isBaseBrand(brand)}
                    isChecked={brand === selectedBrand}
                  />
                )
              })}
            />
          </div>
          <div
            style={{
              display: `flex`,
              flexDirection: `column`,
              gap: `20px`,
            }}
          >
            <Dropdown.Container
              disabled={!isCompatibilityReady}
              SelectedOption={() => <p>{model?.name}</p>}
              Options={modelOptionsList
                .filter((inverter) => inverter.id !== secondaryModel?.id)
                .map((inverter) => {
                  const isBase = isBaseInverter(inverter.id)
                  return (
                    <Dropdown.Item
                      key={inverter.name}
                      onClick={() => {
                        setModel(inverter)
                        if (secondaryModel?.id === inverter.id) {
                          setSecondaryModel(null)
                        }
                      }}
                      label1={inverter.name}
                      isBase={isBase}
                      isChecked={inverter.id === model?.id}
                    />
                  )
                })}
              Label={() => <label>{t(`inverterModel`)}</label>}
            />
            {selectedBrand === `SolarEdge` && (
              <Dropdown.Container
                SelectedOption={() => <p>{secondaryModel?.name ?? `-`}</p>}
                Label={() => <label>{t(`inverterModel2`)}</label>}
                disabled={!isCompatibilityReady || model === null}
                NullableOption={
                  <Dropdown.Item
                    key="nullable-option-for-inverter-model-2"
                    label1="-"
                    isChecked={secondaryModel === null}
                    onClick={() => setSecondaryModel(null)}
                  />
                }
                Options={modelOptionsList
                  .filter((inverter) => inverter.id !== model?.id)
                  .map((inverter) => {
                    const isBase = isBaseInverter(inverter.id)
                    return (
                      <Dropdown.Item
                        key={inverter.name}
                        label1={inverter.name}
                        isBase={isBase}
                        isChecked={inverter.id === secondaryModel?.id}
                        onClick={() => {
                          setSecondaryModel(
                            secondaryModel?.id === inverter.id ? null : inverter,
                          )
                        }}
                      />
                    )
                  })}
              />
            )}
          </div>
        </span>
      </section>
      {isSelectedModuleUnavailable && (
        <div
          className="rounded-md border border-danger/30 bg-danger/5 px-4 py-3 text-sm leading-6 text-foreground"
          role="alert"
        >
          <p className="font-semibold text-danger">
            {t(`moduleUnavailableWarningTitle`)}
          </p>
          <p>{t(`moduleUnavailableWarning`, { module: selectedModule.name })}</p>
        </div>
      )}
      {hasIncompatibleSelection && (
        <ModuleInverterCompatibilityWarning
          moduleName={currentSelectedModule?.name ?? selectedModule.name}
          inverterNames={incompatibleSelectedInverters.map(({ name }) => name)}
        />
      )}
      <SetupMenuFormFooter
        isLoading={modulesQuery.isLoading || updateInverterMutation.isLoading}
        isDirty={
          isCompatibilityReady &&
          !hasIncompatibleSelection &&
          (model?.id !== design?.local?.inverter?.id ||
            secondaryModel?.id !== design?.local?.secondaryInverter?.id)
        }
        isDisabled={
          !isCompatibilityReady ||
          selectedBrand === `` ||
          hasIncompatibleSelection
        }
        onSubmit={async () => {
          if (!isCompatibilityReady || hasIncompatibleSelection) return

          await updateInverterMutation.mutateAsync({
            proposalId: proposal.local.id,
            designId: design.remote.id,
            payload: {
              inverterId: model?.id ?? null,
              inverterManufacturer: selectedBrand,
              secondaryInverterId: secondaryModel?.id ?? null,
              solarModuleId: isSelectedModuleSaved
                ? undefined
                : selectedModule.id,
            },
          })
          if (!isSelectedModuleSaved) {
            setSelectedModuleRemote(selectedModule)
          }
          setSyncWaitingForDesign(true)
        }}
      />
    </MultiChoiceSelectForm>
  )
}

export default InverterSelection
